import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { STATUS_META, fmtNumber, fmtRupiah, fmtDate } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { FileText, Package, Truck, Warehouse, CheckCircle2 } from "lucide-react";

const Row = ({ label, value, mono }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-slate-800/60">
    <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
    <span className={`text-sm text-slate-100 text-right ${mono ? "font-mono" : ""}`}>{value || "-"}</span>
  </div>
);

const TimelineStep = ({ icon: Icon, title, date, sub, done, active }) => (
  <div className="flex gap-3">
    <div className="flex flex-col items-center">
      <div className={`h-8 w-8 rounded-full flex items-center justify-center border-2 ${done ? "border-emerald-500 bg-emerald-500/20 text-emerald-300" : active ? "border-amber-500 bg-amber-500/20 text-amber-300" : "border-slate-700 bg-slate-800/50 text-slate-500"}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className={`w-0.5 flex-1 my-1 ${done ? "bg-emerald-500/50" : "bg-slate-700"}`} />
    </div>
    <div className="pb-6 flex-1">
      <div className="text-sm font-semibold text-slate-100">{title}</div>
      <div className="text-xs text-slate-400 mt-0.5">{date || "Not available yet"}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  </div>
);

export const DetailDrawer = ({ row, open, onOpenChange }) => {
  if (!row) return null;
  const hasPR = !!row.pr_date;
  const hasPO = !!row.po_share_date;
  const hasGR = !!row.date_in_warehouse;
  const isClose = row.status_supply === "CLOSE" || row.status_key === "FULL_SUPPLY";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl bg-[#0B132B] border-slate-800 text-slate-100 overflow-y-auto"
        data-testid="detail-drawer"
      >
        <SheetHeader className="text-left">
          <div className="flex items-center gap-2 mb-1">
            <StatusBadge value={row.status_key} />
            <span className="text-[10px] uppercase tracking-widest text-slate-500">{row.status_supply}</span>
          </div>
          <SheetTitle className="text-slate-100 text-xl">
            {row.po_number || row.pr_number || row.doc_number}
          </SheetTitle>
          <p className="text-sm text-slate-400">{row.description || "-"}</p>
        </SheetHeader>

        <div className="mt-6 grid grid-cols-1 gap-6">
          <div className="rounded-xl border border-slate-800 bg-[#111D38]/60 p-5">
            <div className="text-[11px] uppercase tracking-wider text-amber-400 font-semibold mb-4">Timeline PR → PO → GR</div>
            <TimelineStep icon={FileText} title="Purchase Request (PR)" date={fmtDate(row.pr_date)} sub={row.pr_number} done={hasPR} />
            <TimelineStep icon={Package} title="Purchase Order (PO)" date={fmtDate(row.po_share_date)} sub={row.po_number || "PO not issued"} done={hasPO} active={!hasPO && hasPR} />
            <TimelineStep icon={Truck} title="Estimated Arrival (ETA)" date={fmtDate(row.eta_date)} sub={row.eta_date ? "Parsed from REMARKS" : "Awaiting supplier"} done={!!row.eta_date && hasGR} active={!!row.eta_date && !hasGR} />
            <TimelineStep icon={Warehouse} title="Goods Receipt (GR)" date={fmtDate(row.date_in_warehouse)} sub={hasGR ? "Received at warehouse" : "Not yet received"} done={hasGR && isClose} active={hasGR && !isClose} />
            <TimelineStep icon={CheckCircle2} title="Close / Full Supply" date={isClose ? "Completed" : ""} sub={row.remarks} done={isClose} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <section className="rounded-xl border border-slate-800 bg-[#111D38]/60 p-4">
              <div className="text-[11px] uppercase tracking-wider text-amber-400 font-semibold mb-2">Item Information</div>
              <Row label="Doc Number" value={row.doc_number} mono />
              <Row label="Part No." value={row.product_no} mono />
              <Row label="Description" value={row.description} />
              <Row label="Qty Ordered" value={`${fmtNumber(row.doc_qty)} ${row.uom || ""}`} />
              <Row label="Qty Supplied" value={fmtNumber(row.qty_supply)} />
              <Row label="Qty Outstanding" value={fmtNumber(row.qty_os)} />
            </section>

            <section className="rounded-xl border border-slate-800 bg-[#111D38]/60 p-4">
              <div className="text-[11px] uppercase tracking-wider text-amber-400 font-semibold mb-2">Vendor & Value</div>
              <Row label="Supplier" value={row.supplier} />
              <Row label="Cost Center" value={row.cost_center} />
              <Row label="Department" value={row.department} />
              <Row label="Price per Unit" value={fmtRupiah(row.po_price_unit)} mono />
              <Row label="Total PO Price" value={fmtRupiah(row.total_po_price)} mono />
            </section>

            <section className="rounded-xl border border-slate-800 bg-[#111D38]/60 p-4">
              <div className="text-[11px] uppercase tracking-wider text-amber-400 font-semibold mb-2">Aging (Days)</div>
              <Row label="PR → Quot Email" value={`${fmtNumber(row.aging_pr_quot)} days`} mono />
              <Row label="PR → PO" value={`${fmtNumber(row.aging_pr_po)} days`} mono />
              <Row label="PO → GR" value={`${fmtNumber(row.aging_po_gr)} days`} mono />
              <Row label="PR → GR (Total)" value={`${fmtNumber(row.aging_pr_gr)} days`} mono />
            </section>

            <section className="rounded-xl border border-slate-800 bg-[#111D38]/60 p-4">
              <div className="text-[11px] uppercase tracking-wider text-amber-400 font-semibold mb-2">Notes</div>
              <Row label="Remarks" value={row.remarks} />
              <Row label="Remarks Order" value={row.remarks_order} />
              <Row label="Status Supply" value={row.status_supply} />
              <Row label="ETA (Parsed)" value={fmtDate(row.eta_date)} />
            </section>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
